import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import DeviceOverviewTab from './DeviceOverviewTab';
import DeviceDetailsTab from './DeviceDetailsTab';
import DeviceMaintenanceTab from './DeviceMaintenanceTab';
import DeviceTelemetryTab from './DeviceTelemetryTab';
import DeviceGisMapTab from './gis-map/DeviceGisMapTab';
import ChillerSystemsTab from './ChillerSystemsTab';
import { BarChart3, FileText, WrenchIcon, Activity, MapPin, Thermometer } from 'lucide-react';
import { Device } from './gis-map/types';

interface DeviceHealthTabsProps {
  smartMeters: any[];
  gateways: any[];
  selectedDevice: { id: string; type: string } | null;
  onSelectDevice: (deviceId: string, deviceType: 'smartMeter' | 'gateway' | 'voltageRegulator') => void;
  deviceType: 'energy' | 'gas' | 'water';
}

const DeviceHealthTabs = ({
  smartMeters,
  gateways,
  selectedDevice,
  onSelectDevice,
  deviceType
}: DeviceHealthTabsProps) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [selectedDeviceData, setSelectedDeviceData] = useState<Device | null>(null);

  const handleDeviceSelect = (deviceId: string, deviceType: 'smartMeter' | 'gateway' | 'voltageRegulator', deviceData?: Device) => {
    onSelectDevice(deviceId, deviceType);
    if (deviceData) {
      setSelectedDeviceData(deviceData);
    }
    setActiveTab('details');
  };

  const handleSwitchToDetailsTab = () => {
    setActiveTab('details');
  };

  const getUtilityAccentColor = () => {
    switch (deviceType) {
      case 'gas':
        return 'amber';
      case 'water':
        return 'sky';
      default:
        return 'emerald';
    }
  };

  const accentColor = getUtilityAccentColor();

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
      <div className="flex-none overflow-x-auto">
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="overview" className="flex items-center gap-1 text-xs py-1.5">
            <BarChart3 className="h-3 w-3" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="chillerSystems" className="flex items-center gap-1 text-xs py-1.5">
            <Thermometer className="h-3 w-3" />
            <span className="hidden sm:inline">Chiller Systems</span>
          </TabsTrigger>
          <TabsTrigger value="details" className="flex items-center gap-1 text-xs py-1.5" disabled={!selectedDevice}>
            <FileText className="h-3 w-3" />
            <span className="hidden sm:inline">Details</span>
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="flex items-center gap-1 text-xs py-1.5">
            <WrenchIcon className="h-3 w-3" />
            <span className="hidden sm:inline">Maintenance</span>
          </TabsTrigger>
          <TabsTrigger value="telemetry" className="flex items-center gap-1 text-xs py-1.5">
            <Activity className="h-3 w-3" />
            <span className="hidden sm:inline">Telemetry</span>
          </TabsTrigger>
          <TabsTrigger value="gismap" className="flex items-center gap-1 text-xs py-1.5">
            <MapPin className="h-3 w-3" />
            <span className="hidden sm:inline">GIS Map</span>
          </TabsTrigger>
        </TabsList>
      </div>

      <div className="flex-1 min-h-0 overflow-auto mt-2">
        <TabsContent value="overview" className="h-full m-0 p-0 overflow-visible">
          <DeviceOverviewTab
            filteredSmartMeters={smartMeters}
            filteredGateways={gateways}
            isLoading={false}
            onSelectDevice={handleDeviceSelect}
            selectedFilter={selectedFilter}
            onFilterSelect={setSelectedFilter}
          />
        </TabsContent>

        <TabsContent value="chillerSystems" className="h-full m-0 p-0 overflow-visible">
          <ChillerSystemsTab />
        </TabsContent>

        <TabsContent value="details" className="h-full m-0 p-0 overflow-visible">
          <DeviceDetailsTab 
            selectedDeviceId={selectedDevice?.id || null} 
            utilityType={deviceType}
            deviceHealthData={{
              smartMeters,
              gateways
            }}
            deviceData={selectedDeviceData}
            handleBackToOverview={() => setActiveTab('overview')}
          />
        </TabsContent>

        <TabsContent value="maintenance" className="h-full m-0 p-0 overflow-visible">
          <DeviceMaintenanceTab 
            deviceType={deviceType}
            smartMeters={smartMeters}
            gateways={gateways}
            selectedDevice={selectedDevice}
            deviceData={selectedDeviceData}
          />
        </TabsContent>

        <TabsContent value="telemetry" className="h-full m-0 p-0 overflow-visible">
          <DeviceTelemetryTab 
            deviceType={deviceType}
            selectedDeviceId={selectedDevice?.id || ""}
            selectedDeviceType={selectedDevice?.type || ""}
            deviceData={selectedDeviceData}
          />
        </TabsContent>
        
        <TabsContent value="gismap" className="h-full m-0 p-0 overflow-visible">
          <DeviceGisMapTab 
            deviceType={deviceType} 
            onSelectDevice={handleDeviceSelect}
            onSwitchToDetailsTab={handleSwitchToDetailsTab}
          />
        </TabsContent>
      </div>
    </Tabs>
  );
};

export default DeviceHealthTabs;
